import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSociety } from "@/context/SocietyContext";
import SocietySettingsDialog from "@/components/SocietySettingsDialog";
import api from "@/lib/api";
import {
    House, Users as UsersIcon, Buildings, Receipt, CurrencyInr,
    ChatCircleDots, Megaphone, IdentificationBadge, SignOut, List, Confetti, AddressBook, Gear, HouseLine, Wrench, Car, Garage, ChartLine, FileText, FilePlus, FolderOpen, UserCircle, CheckSquare, Storefront, Handshake, Tag, Toolbox, Truck, CaretRight, UsersThree, Buildings as BuildingsIcon, Handshake as HandshakeIcon, ShieldCheck, ShoppingBag, Coins, CreditCard, WarningCircle, Scroll, ChartBar, CalendarCheck, ListBullets, Broadcast
} from "@phosphor-icons/react";
import Logo, { LogoIcon } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import { fileDownloadUrl } from "@/lib/files";

// ---- Nav model ----------------------------------------------------------
// Accordion pillars: header opens/closes; only ONE open at a time.
// Standalone rows (Overview / Analytics / Profile) render outside the accordion.
// `soon: true` items render as non-clickable "SOON" rows.
const PILLARS = [
    {
        key: "community",
        title: "Community",
        icon: UsersThree,
        items: [
            { to: "/app/flats",     label: "Flats",     icon: Buildings,   roles: ["admin", "committee"],               testId: "nav-flats" },
            { to: "/app/users",     label: "Residents", icon: UsersIcon,    roles: ["admin", "committee"],               testId: "nav-users" },
            { to: "/app/my-flat",   label: "My Flat",   icon: HouseLine,   roles: ["resident", "committee"],            testId: "nav-my-flat" },
            { to: "/app/directory", label: "Directory", icon: AddressBook, roles: ["admin", "committee", "resident"],   testId: "nav-directory" },
            { to: "/app/parking-management", label: "Parking", icon: Garage, roles: ["admin", "committee"],             testId: "nav-parking-mgmt" },
            // Family — planned, hidden until built.
        ],
    },
    {
        key: "finance",
        title: "Finance",
        icon: CurrencyInr,
        items: [
            { to: "/app/maintenance", label: "Maintenance", icon: Coins, roles: ["admin", "committee"], testId: "nav-maintenance" },
            { to: "/app/invoices", label: "Invoices",    icon: Receipt,     roles: ["admin", "committee", "resident"], testId: "nav-invoices" },
            { to: "/app/payments", label: "Payments",    icon: CreditCard,  roles: ["admin", "committee"], testId: "nav-payments" },
            { to: "/app/expenses", label: "Expenses",    icon: CurrencyInr, roles: ["admin", "committee", "resident"], testId: "nav-expenses" },
            { to: "/app/defaulters", label: "Defaulters", icon: WarningCircle, roles: ["admin", "committee"], testId: "nav-defaulters" },
            { to: "/app/receipts", label: "Receipts", icon: Scroll, roles: ["admin", "committee"], testId: "nav-receipts" },
            { to: "/app/my-receipts", label: "My Receipts", icon: Scroll, roles: ["resident"], testId: "nav-my-receipts" },
            { to: "/app/financial-reports", label: "Financial Reports", icon: ChartBar, roles: ["admin", "committee"], testId: "nav-financial-reports" },
        ],
    },
    {
        key: "operations",
        title: "Operations",
        icon: HandshakeIcon,
        items: [
            { to: "/app/complaints", label: "Complaints",      icon: ChatCircleDots, roles: ["admin", "committee", "resident"], testId: "nav-complaints" },
            { to: "/app/staff",      label: "Staff & Vendors", icon: Wrench,         roles: ["admin", "committee", "resident"], testId: "nav-staff" },
            { to: "/app/work-orders", label: "Work Orders",    icon: Toolbox,        roles: ["admin", "committee"],             testId: "nav-work-orders" },
            { to: "/app/preventive-maintenance", label: "Preventive Maintenance", icon: CalendarCheck, roles: ["admin", "committee"], testId: "nav-preventive-maintenance" },
            { to: "/app/amenities",  label: "Amenities",       icon: Confetti,       roles: ["admin", "committee", "resident"], testId: "nav-amenities" },
            // Work Orders — planned, hidden until built.
        ],
    },
    {
        key: "gate",
        title: "Gate",
        icon: ShieldCheck,
        items: [
            { to: "/app/visitors",            label: "Visitors",    icon: IdentificationBadge, roles: ["committee", "resident"], testId: "nav-visitors" },
            { to: "/app/visitor-logs",         label: "Visitor Logs", icon: ListBullets,        roles: ["admin", "committee"],             testId: "nav-visitor-logs" },
            { to: "/app/my-vehicles",         label: "My Vehicles", icon: Car,                 roles: ["resident", "committee"],          testId: "nav-my-vehicles" },
            { to: "/app/vehicles",             label: "Vehicles", icon: CheckSquare,            roles: ["admin", "committee"],             testId: "nav-vehicles" },
            { to: "/app/vehicle-logs",         label: "Vehicle Logs", icon: Car,                roles: ["admin", "committee"],             testId: "nav-vehicle-logs" },
            { to: "/app/rfid-readers",         label: "RFID Readers", icon: Broadcast,          roles: ["admin", "committee"],             testId: "nav-rfid-readers" },
            // Deliveries, Security — planned, hidden until built.
        ],
    },
    {
        key: "governance",
        title: "Governance",
        icon: BuildingsIcon,
        items: [
            { to: "/app/announcements",       label: "Notices",       icon: Megaphone,   roles: ["admin", "committee", "resident"], testId: "nav-announcements" },
            { to: "/app/voting-management",   label: "Voting",        icon: CheckSquare, roles: ["admin", "committee"],             testId: "nav-voting-mgmt" },
            { to: "/app/voting",              label: "Voting",        icon: CheckSquare, roles: ["resident"],                       testId: "nav-voting" },
            { to: "/app/document-library",    label: "Documents",     icon: FolderOpen,  roles: ["admin", "committee", "resident"], testId: "nav-doc-library" },
            { to: "/app/document-templates",  label: "Doc Templates", icon: FileText,    roles: ["admin", "committee"],             testId: "nav-doc-templates" },
            { to: "/app/document-generation", label: "Generate Doc",  icon: FilePlus,    roles: ["admin", "committee"],             testId: "nav-doc-generation" },
            // Meetings, Resolutions — planned, hidden until built.
        ],
    },
    {
        key: "commerce",
        title: "Community Commerce",
        icon: ShoppingBag,
        subtitle: "Coming soon",
        items: [
            { label: "Local Shops",   icon: Storefront, roles: ["admin", "committee", "resident"], soon: true, testId: "nav-soon-local-shops" },
            { label: "Services",      icon: Handshake,  roles: ["admin", "committee", "resident"], soon: true, testId: "nav-soon-services" },
            { label: "Deals",         icon: Tag,        roles: ["admin", "committee", "resident"], soon: true, testId: "nav-soon-deals" },
            { label: "Home Services", icon: Toolbox,    roles: ["admin", "committee", "resident"], soon: true, testId: "nav-soon-home-services" },
            { label: "Delivery",      icon: Truck,      roles: ["admin", "committee", "resident"], soon: true, testId: "nav-soon-delivery" },
        ],
    },
];

const GLOBAL_ITEM = { to: "/app/parking-analytics", label: "Analytics", icon: ChartLine, roles: ["admin", "committee"], testId: "nav-parking-analytics" };
const OVERVIEW_ITEM = { to: "/app", label: "Overview", icon: House, roles: ["admin", "committee", "resident"], end: true, testId: "nav-overview" };
const PROFILE_ITEM = { to: "/app/profile", label: "My Profile", icon: UserCircle, roles: ["admin", "committee", "resident"], testId: "nav-profile" };

export default function Layout() {
    const { user, logout } = useAuth();
    const { society } = useSociety();
    const nav = useNavigate();
    const location = useLocation();

    const [mobileOpen, setMobileOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [openPillar, setOpenPillar] = useState(null);
    const [impersonating] = useState(() => {
        try { return JSON.parse(localStorage.getItem("maintayn_impersonating") || "null"); } catch { return null; }
    });

    async function exitImpersonation() {
        try { await api.post("/master/exit-impersonation"); } catch { /* best-effort */ }
        const masterToken = localStorage.getItem("maintayn_master_token_backup");
        localStorage.removeItem("maintayn_impersonating");
        localStorage.removeItem("maintayn_master_token_backup");
        if (masterToken) {
            localStorage.setItem("maintayn_token", masterToken);
        } else {
            localStorage.removeItem("maintayn_token");
        }
        window.location.href = masterToken ? "/master" : "/login";
    }

    // Role-filtered pillars. Empty ones vanish entirely (e.g. Gate has no items → hidden).
    const visiblePillars = useMemo(() =>
        PILLARS
            .map((p) => ({ ...p, items: p.items.filter((it) => it.roles.includes(user?.role)) }))
            .filter((p) => p.items.length > 0),
        [user?.role]
    );

    // Auto-expand the pillar that owns the current route. On any route change we
    // resync — matches spec: "browser refresh preserves correct parent expansion".
    useEffect(() => {
        const owner = visiblePillars.find((p) => p.items.some((it) => it.to && it.to === location.pathname));
        setOpenPillar(owner ? owner.key : null);
    }, [location.pathname, visiblePillars]);

    const togglePillar = (key) => setOpenPillar((cur) => (cur === key ? null : key));

    const societyName = society?.name || "maintayn";
    const societyLogoUrl = fileDownloadUrl(society?.logo_file_id);
    const isAdmin = user?.role === "admin";
    // ADMIN/COMMITTEE = charcoal command-center sidebar. RESIDENT = lighter, calmer, off-white sidebar.
    // Both share the same brand family (orange active indicator) but read visually distinct.
    const isResident = user?.role === "resident";

    const overviewVisible = OVERVIEW_ITEM.roles.includes(user?.role);
    const analyticsVisible = GLOBAL_ITEM.roles.includes(user?.role);
    const profileVisible = PROFILE_ITEM.roles.includes(user?.role);

    // The pillar that owns the current route — used to emphasize its header.
    const activePillarKey = useMemo(() => {
        const owner = visiblePillars.find((p) => p.items.some((it) => it.to && it.to === location.pathname));
        return owner ? owner.key : null;
    }, [visiblePillars, location.pathname]);

    return (
        <>
            {/* Security guards get a bare, mobile-first Gate operational surface — no
                admin/committee/resident sidebar chrome (Gate V2 spec: /app/qr-scanner is
                their dedicated home; each Gate page renders its own compact header). */}
            {user?.role === "security" ? (
                <Outlet />
            ) : (
            <>
            {impersonating && (
                <div data-testid="impersonation-banner" className="w-full bg-brand-action text-white text-sm px-4 py-2 flex items-center justify-center gap-3 flex-wrap sticky top-0 z-50">
                    <span className="font-semibold uppercase tracking-overline text-[10px] bg-white/20 rounded-full px-2 py-0.5">Master Console</span>
                    <span>You are viewing <strong>{impersonating.society_name}</strong> as Admin.</span>
                    <button data-testid="exit-impersonation-btn" onClick={exitImpersonation} className="underline font-semibold hover:text-white/80">
                        Exit Impersonation
                    </button>
                </div>
            )}
            <div className="min-h-screen flex bg-brand-bg">
            {/* Mobile backdrop */}
            {mobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-30 bg-brand-ink/50 backdrop-blur-sm"
                    onClick={() => setMobileOpen(false)}
                    data-testid="sidebar-mobile-backdrop"
                />
            )}
            {/* Sidebar */}
            <aside
                data-testid="app-sidebar"
                data-sidebar-variant={isResident ? "resident" : "admin"}
                className={`${mobileOpen ? "block" : "hidden"} lg:block fixed lg:static inset-y-0 left-0 z-40 w-64 flex flex-col ${
                    isResident ? "bg-white border-r border-brand-line" : "bg-brand-ink"
                }`}
            >
                <div className={`px-6 py-6 border-b ${isResident ? "border-brand-line" : "border-white/10"}`}>
                    <div className="flex items-center gap-2">
                        {societyLogoUrl ? (
                            <img src={societyLogoUrl} alt="logo" data-testid="sidebar-society-logo"
                                className={`w-8 h-8 rounded-md object-contain p-0.5 ${isResident ? "bg-brand-bg" : "bg-white/10"}`} />
                        ) : (
                            <LogoIcon size={26} variant={isResident ? "onLight" : "onDark"} />
                        )}
                        <div className="min-w-0 flex-1">
                            <p className={`font-heading font-bold text-lg tracking-tight truncate ${isResident ? "text-brand-ink" : "text-white"}`} data-testid="sidebar-society-name">
                                {societyName}
                            </p>
                            <p className={`text-[10px] font-medium uppercase tracking-overline truncate ${isResident ? "text-brand-inkSoft" : "text-gray-400"}`}>
                                on maintayn · Community OS
                            </p>
                        </div>
                    </div>
                </div>

                <nav className="flex-1 px-3 pt-3 pb-3 overflow-y-auto" data-testid="sidebar-nav">
                    {overviewVisible && <TopLink item={OVERVIEW_ITEM} resident={isResident} onNavigate={() => setMobileOpen(false)} />}

                    <div className="mt-2 space-y-1">
                        {visiblePillars.map((pillar) => (
                            <Pillar
                                key={pillar.key}
                                pillar={pillar}
                                resident={isResident}
                                open={openPillar === pillar.key}
                                active={activePillarKey === pillar.key}
                                onToggle={() => togglePillar(pillar.key)}
                                onNavigate={() => setMobileOpen(false)}
                            />
                        ))}
                    </div>

                    {analyticsVisible && (
                        <div className={`mt-3 pt-3 border-t ${isResident ? "border-brand-line" : "border-white/10"}`}>
                            <TopLink item={GLOBAL_ITEM} resident={isResident} onNavigate={() => setMobileOpen(false)} />
                        </div>
                    )}
                </nav>

                {/* Account area */}
                <div className={`px-3 py-3 border-t ${isResident ? "border-brand-line" : "border-white/10"}`} data-testid="sidebar-account">
                    {profileVisible && <TopLink item={PROFILE_ITEM} resident={isResident} onNavigate={() => setMobileOpen(false)} />}
                    {isAdmin && (
                        <button
                            data-testid="sidebar-society-settings"
                            onClick={() => setSettingsOpen(true)}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-gray-400 hover:bg-brand-forest hover:text-white transition-colors duration-150"
                        >
                            <Gear size={16} weight="duotone" />
                            <span>Society Settings</span>
                        </button>
                    )}
                    <button
                        data-testid="sidebar-logout"
                        onClick={async () => { await logout(); nav("/login"); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 hover:bg-[#EF4444] hover:text-white ${isResident ? "text-brand-inkSoft" : "text-gray-400"}`}
                    >
                        <SignOut size={16} weight="duotone" />
                        <span>Sign Out</span>
                    </button>

                    <div className={`mt-3 px-3 text-[10px] font-medium uppercase tracking-overline truncate ${isResident ? "text-brand-inkSoft" : "text-gray-400"}`} data-testid="sidebar-user-name">
                        {user?.name} · <span className="text-brand-action">{user?.role}</span>
                    </div>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0">
                <header className="h-16 bg-white border-b border-brand-line flex items-center justify-between px-4 lg:px-8">
                    <div className="flex items-center gap-3">
                        <button
                            className="lg:hidden p-2 rounded-md border border-brand-line hover:bg-brand-bg transition-colors"
                            onClick={() => setMobileOpen(!mobileOpen)}
                            data-testid="mobile-menu-toggle"
                        >
                            <List size={18} />
                        </button>
                        <div className="lg:hidden"><Logo /></div>
                        <div className="hidden lg:block">
                            <p className="text-[10px] font-semibold uppercase tracking-overline text-brand-inkSoft" data-testid="header-society-name">{societyName}</p>
                            <p className="font-heading font-semibold text-brand-ink">Welcome back, {user?.name?.split(" ")[0]}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="hidden md:inline text-xs font-medium uppercase tracking-overline text-brand-inkSoft">
                            {new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                        </span>
                        <NotificationBell />
                    </div>
                </header>

                <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
                    <Outlet />
                </main>
            </div>
            <SocietySettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
            </div>
            </>
            )}
        </>
    );
}

// ---- Sub-components -----------------------------------------------------
function TopLink({ item, resident, onNavigate }) {
    const Icon = item.icon;
    return (
        <NavLink
            to={item.to}
            end={item.end}
            data-testid={item.testId}
            onClick={onNavigate}
            className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                    isActive
                        ? resident
                            ? "text-brand-ink font-semibold bg-brand-sage border-l-[3px] border-brand-action pl-[9px]"
                            : "bg-brand-forest text-white border-l-[3px] border-brand-action pl-[9px]"
                        : resident
                            ? "text-gray-500 hover:bg-brand-bg hover:text-brand-ink"
                            : "text-gray-400 hover:bg-brand-forest hover:text-white"
                }`
            }
        >
            <Icon size={16} weight="duotone" />
            <span className="truncate">{item.label}</span>
        </NavLink>
    );
}

function Pillar({ pillar, resident, open, active, onToggle, onNavigate }) {
    const HeaderIcon = pillar.icon;
    const panelId = `pillar-panel-${pillar.key}`;
    // Emphasize the header when the current route lives inside this pillar.
    // Subtle: bolder text + soft background + orange left rail. No color change on hover-only.
    const headerState = active
        ? resident
            ? "text-brand-ink font-semibold bg-brand-sage border-l-[3px] border-brand-action pl-[9px]"
            : "text-white bg-brand-forest/70 border-l-[3px] border-brand-action pl-[9px]"
        : resident
            ? "text-gray-600 hover:bg-brand-bg hover:text-brand-ink"
            : "text-gray-300 hover:bg-brand-forest hover:text-white";
    return (
        <div data-testid={`sidebar-pillar-${pillar.key}`}>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={panelId}
                data-testid={`pillar-header-${pillar.key}`}
                data-open={open ? "true" : "false"}
                data-active={active ? "true" : "false"}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${headerState}`}
            >
                <HeaderIcon size={16} weight="duotone" className="shrink-0" />
                <span className="flex-1 text-left truncate min-w-0">{pillar.title}</span>
                <CaretRight
                    size={12}
                    weight="bold"
                    className={`shrink-0 transition-transform duration-200 ease-out ${resident ? "text-gray-400" : "text-gray-500"} ${open ? "rotate-90" : "rotate-0"}`}
                />
            </button>

            <div
                id={panelId}
                data-testid={`pillar-panel-${pillar.key}`}
                className={`grid transition-[grid-template-rows] duration-200 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
            >
                <div className="overflow-hidden">
                    <div className="pt-1 pb-1 pl-3 space-y-[2px]">
                        {pillar.items.map((it) => {
                            const Icon = it.icon;
                            if (it.soon) {
                                return (
                                    <div
                                        key={it.testId}
                                        data-testid={it.testId}
                                        aria-disabled="true"
                                        title="Coming soon"
                                        className={`flex items-center gap-3 pl-6 pr-3 py-1.5 rounded-md text-[13px] cursor-not-allowed select-none ${resident ? "text-gray-400" : "text-gray-500"}`}
                                    >
                                        <Icon size={14} weight="duotone" />
                                        <span className="flex-1 truncate">{it.label}</span>
                                        <span className="text-[9px] font-semibold uppercase tracking-widest text-brand-action/70 border border-brand-action/30 rounded-full px-1.5 py-[1px]">
                                            Soon
                                        </span>
                                    </div>
                                );
                            }
                            return (
                                <NavLink
                                    key={it.to}
                                    to={it.to}
                                    data-testid={it.testId}
                                    onClick={onNavigate}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 pl-6 pr-3 py-1.5 rounded-md text-[13px] transition-colors duration-150 ${
                                            isActive
                                                ? resident
                                                    ? "text-brand-ink font-semibold bg-brand-sage border-l-[3px] border-brand-action pl-[21px]"
                                                    : "text-white bg-brand-forest border-l-[3px] border-brand-action pl-[21px]"
                                                : resident
                                                    ? "text-gray-500 hover:bg-brand-bg hover:text-brand-ink"
                                                    : "text-gray-400 hover:bg-brand-forest hover:text-white"
                                        }`
                                    }
                                >
                                    <Icon size={14} weight="duotone" />
                                    <span className="truncate">{it.label}</span>
                                </NavLink>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
